import Preview from "./Preview/Preview";


const HashRouteContextProps=[
    {
        name:"data",
        type:"object",
        description:"data object passed to the route",
    },
    {
        name:"params",
        type:"object",
        description:`
            route params object, the param name as key 
            @example
            @hash <span>#:things#:name</span> params <span>{things,name}</span>
        `,
    },
    {
        name:"location",
        type:"{hash:string,url:string}",
        description:"extra data about the current route location",
    },
]

const data={
    name:"data",
    type:"object",
    description:`
        Data object to pass to the new route component
        @accessible from the route component via props.data
        @You can use history/location global object for navigation, but to pass data to routes you
        need to use the methods above
    `,
}

export default {
    id:"HashRouter",
    type:"(options:object):HashRouter",
    //preview:Preview,
    description:`
        A HashRouter with nested hash params support
        @Only one route is shown at a time
        @Prioritizes exact matches
        @if more than one route matches the hash, the first one is picked
    `,
    options:[
        {
            name:"target",
            type:"HTMLElement",
            required:true,
            description:"Element in which the route component is inserted",
        },
        {
            name:"routes",
            type:"HashRoute[]",
            props:[
                {
                    name:"HashRoute",
                    props:[
                        {
                            name:"hash",
                            type:"string",
                            required:true,
                            description:`route hash, example : "#one"`,
                            tip:`
                                for hash params use <b>#:</b>
                                @You can nest as many params as you need: <b>#category#:things#:name</b>, and then you can
                                access the values in the route component props.
                                @for a default route, set hash to an empty string
                            `,
                        },
                        {
                            name:"memorize",
                            type:"boolean",
                            description:`
                                If true, the route component is rendered once, using the Element returned by the function
                                for next matches
                                @use it when the route is static (same UI everytime)
                            `,
                            notice:`
                                component prop must return a HTMLElement for this prop to take effect
                                @changes in hash params, will trigger a rerender even when this prop is set to true
                            `,
                        },
                        {
                            name:"restrictor",
                            type:"(unlock:function,target:HTMLElement):void",
                            description:`
                                If specified, the route won't show only if restrictor unlocks the route
                                @to unlock it , the restrictor must call the unlock param function with unlocked param as true
                                @if undefined, the route is unlocked by default
                            `,
                            props:[
                                {
                                    name:"unlock",
                                    type:"(unlocked:boolean):void",
                                    description:`
                                        unlocks the route when called with unlocked true
                                    `,
                                },
                                {
                                    name:"target",
                                    type:"HTMLElement",
                                    description:`
                                        the target prop value
                                        @You may need it if you want to show for example a loading view
                                        until the route is unlocked and shown
                                    `,
                                },
                            ],
                        },
                        {
                            name:"component",
                            type:"(props:object):HashRouteElement",
                            required:true,
                            props:[
                                {
                                    name:"props",
                                    type:"object",
                                    props:[
                                        {
                                            name:"parent",
                                            type:"HTMLElement",
                                            description:"the target prop value",
                                        },
                                        ...HashRouteContextProps,
                                    ],
                                },
                                {
                                    name:"HashRouteElement",
                                    type:"HTMLElement",
                                    props:[{
                                        name:"onShow",type:"():void",
                                        description:`
                                            a method attached to the element that 
                                            is called every time the route is shown
                                        `,
                                    }],
                                }
                            ],
                        },
                    ],
                }
            ],
        },
    ],
    methods:[
        {
            name:"push",
            type:"(hash:string,data:object):void",
            description:"Adds an entry to the browser's session history stack",
        },
        {
            name:"append",
            type:"(hash:string,data:object):void",
            description:"appends the hash to the end of the current hash",
        },
        {
            name:"replace",
            type:"(hash:string,data:object):void",
            description:"replaces the current history entry",
            props:[data],
        },
        {
            name:"refresh",
            type:"():void",
            description:"rerenders the current route even if memorize true is specified",
            notice:`
                for other navigation features, just use the 
                <a href="https://developer.mozilla.org/en-US/docs/Web/API/History">History API</a>
            `,
        },
    ],
};
